import React, {useMemo, useState} from 'react';

import {SetupSectionProps, getStateViewDefaults, setupWizardApi} from 'src/lib/api/setupWizard';
import {buttonSecondary} from 'src/lib/tailwindClasses';

import {ApiErrorMessage} from './ApiErrorMessage';
import {CliCommandBlock} from './CliCommandBlock';
import {PrerequisiteWarning} from './SetupStepSection';
import {downloadBlob} from './shared';
import {useSetupMutation} from './useSetupMutation';

export default function SaveExportSection({
  orgId,
  projectId,
  payload,
  queryKey,
  prerequisiteWarning,
}: SetupSectionProps) {
  const view = useMemo(() => getStateViewDefaults(payload?.data), [payload?.data]);
  const state = payload?.data;
  const {
    mutate,
    mutatingAction,
    isMutating,
    error: mutationError,
    lastCliCommand,
  } = useSetupMutation(queryKey);

  const [isDownloadingCsv, setIsDownloadingCsv] = useState(false);
  const [selectedSites, setSelectedSites] = useState<Set<string>>(new Set());

  // Sites that have uploaded observations
  const sitesWithObs = useMemo(() => {
    return (payload?.data?.uploadedObsSummary ?? [])
      .filter((s) => s.hasData)
      .map((s) => s.siteName);
  }, [payload?.data?.uploadedObsSummary]);

  // Initialize selected sites when sitesWithObs changes
  React.useEffect(() => {
    setSelectedSites(new Set(sitesWithObs));
  }, [sitesWithObs]);

  const handleDownloadSetupCsv = async () => {
    setIsDownloadingCsv(true);
    try {
      const blob = await setupWizardApi.downloadSetupCsv(orgId, projectId);
      downloadBlob(blob, `${projectId}_setup.csv`);
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Download failed');
    } finally {
      setIsDownloadingCsv(false);
    }
  };

  const toggleSite = (site: string) => {
    setSelectedSites((prev) => {
      const next = new Set(prev);
      if (next.has(site)) {
        next.delete(site);
      } else {
        next.add(site);
      }
      return next;
    });
  };

  const handleCommitDelineation = async () => {
    await mutate('commit-delineation', () => setupWizardApi.commitDelineation(orgId, projectId));
  };

  const handleCommitGaugeData = async () => {
    await mutate('commit-gauge-data', () =>
      setupWizardApi.commitGaugeData(orgId, projectId, {
        siteNames: Array.from(selectedSites),
      })
    );
  };

  const hasSites = view.siteInfo.length > 0;
  const hasDelineation = view.hasDelineation;
  const alreadyCommitted = state?.committedFcid != null;
  const storedCommitOutput = state?.commitOutput ?? null;
  const gaugeCommitOutput = state?.gaugeCommitOutput ?? null;
  const gaugeCommittedSites = state?.gaugeCommittedSites ?? [];

  if (!hasSites) {
    return (
      <div>
        {prerequisiteWarning && <PrerequisiteWarning message={prerequisiteWarning} />}
        {!prerequisiteWarning && (
          <p className="text-sm text-gray-500">Define sites before using Save & Export.</p>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <ApiErrorMessage message={mutationError} />
      <CliCommandBlock command={lastCliCommand} />

      {/* Section A: Download Complete Setup CSV */}
      <div>
        <h3 className="text-sm font-medium text-gray-900 mb-2">Download Complete Setup CSV</h3>
        <p className="text-xs text-gray-600 mb-2">
          Merges site info, obs config, and cleanup params into the canonical setup CSV format with
          computed columns.
        </p>
        <button
          type="button"
          disabled={isDownloadingCsv || view.siteInfo.length === 0}
          className={buttonSecondary}
          onClick={handleDownloadSetupCsv}
        >
          {isDownloadingCsv ? 'Downloading…' : 'Download Setup CSV'}
        </button>
      </div>

      {/* Section B: Commit Delineation */}
      <div className="border-t border-gray-200 pt-4">
        <h3 className="text-sm font-medium text-gray-900 mb-2">Step 1: Commit Delineation</h3>
        <p className="text-xs text-gray-600 mb-3">
          Runs delineation with <code>commit=True</code> to create the FeatureCollection and
          SiteList in the database. This must be done before committing gauge data.
        </p>

        {!hasDelineation && (
          <PrerequisiteWarning message="Run delineation in the Run Delineation step first." />
        )}

        {alreadyCommitted ? (
          <div className="bg-green-50 border border-green-200 rounded-md p-3 mb-3">
            <p className="text-sm text-green-800">
              Delineation committed.
              {state?.newSiteListId && (
                <>
                  {' '}
                  Site list: <code className="font-mono text-xs">{state.newSiteListId}</code>
                </>
              )}
              {state?.committedFcid != null && (
                <>
                  {' '}
                  | Feature Collection ID:{' '}
                  <code className="font-mono text-xs">{state.committedFcid}</code>
                </>
              )}
            </p>
          </div>
        ) : (
          <button
            type="button"
            disabled={isMutating || !hasDelineation}
            className="rounded-md bg-green-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-500 disabled:opacity-50"
            onClick={handleCommitDelineation}
          >
            {mutatingAction === 'commit-delineation' ? 'Committing…' : 'Commit Delineation'}
          </button>
        )}
      </div>

      {/* Delineation commit output (persisted on GCS) */}
      {storedCommitOutput && (
        <div className="border border-gray-200 rounded-md p-3 bg-gray-50">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-xs font-medium text-gray-700">Delineation Commit Output</h4>
            <button
              type="button"
              className="text-xs text-blue-600 hover:text-blue-800"
              onClick={() => navigator.clipboard.writeText(storedCommitOutput)}
            >
              Copy to clipboard
            </button>
          </div>
          <pre className="text-xs overflow-auto max-h-64 whitespace-pre-wrap">
            {storedCommitOutput}
          </pre>
        </div>
      )}

      {/* Section C: Commit Gauge Data */}
      <div className="border-t border-gray-200 pt-4">
        <h3 className="text-sm font-medium text-gray-900 mb-2">Step 2: Commit Gauge Data</h3>
        <p className="text-xs text-gray-600 mb-3">
          Ingests observation time series for selected sites into the database.
        </p>

        {!alreadyCommitted && (
          <PrerequisiteWarning message="Commit delineation (Step 1) before committing gauge data." />
        )}

        {alreadyCommitted && sitesWithObs.length > 0 && (
          <div className="mb-3">
            <p className="text-xs font-medium text-gray-700 mb-1">
              Select sites to commit gauge data for:
            </p>
            <div className="max-h-40 overflow-auto border border-gray-200 rounded-md p-2 space-y-1">
              {sitesWithObs.map((site) => (
                <label key={site} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={selectedSites.has(site)}
                    onChange={() => toggleSite(site)}
                    className="rounded border-gray-300"
                  />
                  {site}
                </label>
              ))}
            </div>
          </div>
        )}

        {alreadyCommitted && sitesWithObs.length === 0 && (
          <p className="text-xs text-gray-500 mb-3">No sites have uploaded observations.</p>
        )}

        <button
          type="button"
          disabled={isMutating || !alreadyCommitted || selectedSites.size === 0}
          className="rounded-md bg-green-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-500 disabled:opacity-50"
          onClick={handleCommitGaugeData}
        >
          {mutatingAction === 'commit-gauge-data' ? 'Committing…' : 'Commit Gauge Data'}
        </button>
      </div>

      {/* Gauge commit output (persisted on GCS) */}
      {(gaugeCommitOutput || gaugeCommittedSites.length > 0) && (
        <div className="border-t border-gray-200 pt-4">
          <h3 className="text-sm font-medium text-gray-900 mb-2">Gauge Data Commit Output</h3>
          {gaugeCommittedSites.length > 0 && (
            <p className="text-xs text-green-700 mb-2">
              Gauge data committed for: {gaugeCommittedSites.join(', ')}
            </p>
          )}
          {gaugeCommitOutput && (
            <pre className="text-xs bg-gray-50 border border-gray-200 rounded-md p-3 overflow-auto max-h-64 whitespace-pre-wrap">
              {gaugeCommitOutput}
            </pre>
          )}
        </div>
      )}
    </div>
  );
}
